package devxertech.com.pages.services.chatbotDevelopment

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaComments
import com.varabyte.kobweb.silk.components.icons.fa.FaDiagramProject
import com.varabyte.kobweb.silk.components.icons.fa.FaMinus
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaUserCheck
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import devxertech.com.pages.blogs.SanityBlogPost
import devxertech.com.pages.blogs.fetchPublishedPosts
import devxertech.com.pages.blogs.prefetchPublishedPost
import devxertech.com.utils.imageLoading
import org.w3c.dom.events.EventListener
import devxertech.com.section.DesktopNavigationAnchor
import devxertech.com.section.onDesktopNavigationClick
import devxertech.com.theme.SitePalette
import devxertech.com.utils.Constants
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.dom.*

private data class ChatbotCopy(val heading: String, val body: String)

private val assistantUseCases = listOf(
    ChatbotCopy(
        "Website Inquiries & Customer Support",
        "Help visitors find service information and answers from your approved business content. Define when the assistant should refer a question to your team."
    ),
    ChatbotCopy(
        "Lead Qualification",
        "Guide visitors through relevant questions and collect the information your team needs for follow-up. CRM integrations can be scoped according to your platform and available access."
    ),
    ChatbotCopy(
        "Business-Specific Workflows",
        "Plan an assistant around the tasks your business needs to support. Connections to calendars, forms and other tools depend on platform access and technical feasibility, which we confirm during discovery."
    )
)

private val assistantFaqs = listOf(
    ChatbotCopy(
        "Can the chatbot use our website content and documents?",
        "We can scope an assistant around approved website content, FAQs and documents. Supported formats and update methods depend on the solution."
    ),
    ChatbotCopy(
        "Can it connect to our CRM or other tools?",
        "Integrations depend on your platform’s APIs, permissions and workflow. We confirm feasibility before including them in the project scope."
    ),
    ChatbotCopy(
        "What happens when it cannot answer?",
        "We define fallback behavior, such as asking for clarification or directing the visitor to your team."
    ),
    ChatbotCopy(
        "How much does a custom AI chatbot cost?",
        "Cost depends on the use case, content, integrations, testing and support. Hosting and AI usage costs should also be considered. We provide a quote after reviewing your requirements."
    ),
    ChatbotCopy(
        "Can we update it after launch?",
        "The content update process is agreed during development. Maintenance and support can be included according to your needs."
    )
)

private data class ChatbotResource(
    val slug: String,
    val title: String,
    val description: String,
)

private val chatbotResources = listOf(
    ChatbotResource(
        "ai-chatbot-development-cost",
        "AI Chatbot Development Cost",
        "Explore the factors that shape the cost of planning and building a custom AI chatbot.",
    ),
    ChatbotResource(
        "ai-chatbots-customer-support-human-handoff",
        "AI Chatbots for Customer Support",
        "Learn how customer support assistants can handle inquiries and pass conversations to a human when needed.",
    ),
    ChatbotResource(
        "ai-chatbot-vs-workflow-automation",
        "AI Chatbot vs Workflow Automation",
        "Compare conversational assistants with workflow automation to choose a useful first project.",
    ),
)

@Composable
fun BusinessAssistantServices(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(if (breakpoint <= Breakpoint.MD) 36.px else 60.px, if (breakpoint <= Breakpoint.MD) 20.px else 40.px)
            backgroundColor(palette.bodyBackgroundSecondary.rgb)
            color(palette.commonSecondaryButtonColor.rgb)
        }
    }) {
        Div(attrs = { style { property("max-width", "1100px"); property("margin", "0 auto") } }) {
            SectionHeading("AI Assistants Built Around Your Business", breakpoint)
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", if (breakpoint <= Breakpoint.MD) "minmax(0, 1fr)" else "repeat(3, minmax(0, 1fr))")
                    property("gap", if (breakpoint <= Breakpoint.MD) "16px" else "24px")
                }
            }) {
                assistantUseCases.forEachIndexed { index, item ->
                    val iconLabel = listOf("Customer support", "Lead qualification", "Business workflow")[index]
                    Div(attrs = {
                        attr("role", "group")
                        style {
                            property("box-sizing", "border-box")
                            width(100.percent)
                            padding(if (breakpoint <= Breakpoint.MD) 20.px else 26.px)
                            borderRadius(10.px)
                            backgroundColor(palette.bodyBackgroundSecondary.rgb)
                            property("border", "1px solid rgba(37, 77, 121, 0.14)")
                            property("box-shadow", "0 8px 24px rgba(37, 77, 121, 0.06)")
                        }
                    }) {
                        Div(attrs = {
                            attr("aria-hidden", "true")
                            attr("title", iconLabel)
                            style {
                                display(DisplayStyle.Flex)
                                property("align-items", "center")
                                property("justify-content", "center")
                                width(48.px)
                                height(48.px)
                                margin(0.px, 0.px, 18.px)
                                borderRadius(12.px)
                                property("background-color", "rgba(21, 157, 164, 0.10)")
                                color(palette.primary.rgb)
                                property("font-size", "22px")
                            }
                        }) {
                            when (index) {
                                0 -> FaComments()
                                1 -> FaUserCheck()
                                else -> FaDiagramProject()
                            }
                        }
                        H3(attrs = {
                            style {
                                margin(0.px, 0.px, 12.px)
                                fontFamily(Constants.ROBOTO_FONT)
                                fontSize(if (breakpoint <= Breakpoint.MD) 19.px else 21.px)
                                fontWeight(700)
                                lineHeight("1.35")
                            }
                        }) { Text(item.heading) }
                        P(attrs = {
                            style {
                                margin(0.px)
                                fontFamily(Constants.FONT_FAMILY)
                                fontSize(16.px)
                                lineHeight("1.7")
                            }
                        }) { Text(item.body) }
                    }
                }
            }
        }
    }
}

@Composable
fun HelpfulChatbotResources(breakpoint: Breakpoint, palette: SitePalette) {
    Section(attrs = {
        id("chatbot-helpful-resources")
        attr("aria-labelledby", "chatbot-helpful-resources-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(if (breakpoint <= Breakpoint.MD) 28.px else 40.px, if (breakpoint <= Breakpoint.MD) 20.px else 40.px)
            backgroundColor(palette.bodyBackgroundSecondary.rgb)
            color(palette.commonSecondaryButtonColor.rgb)
        }
    }) {
        Div(attrs = { style { property("max-width", "900px"); property("margin", "0 auto") } }) {
            H2(attrs = {
                attr("id", "chatbot-helpful-resources-heading")
                style {
                    margin(0.px, 0.px, if (breakpoint <= Breakpoint.MD) 18.px else 24.px)
                    fontFamily(Constants.ROBOTO_FONT)
                    fontSize(if (breakpoint <= Breakpoint.MD) 23.px else 28.px)
                    fontWeight(700)
                    lineHeight("1.25")
                }
            }) { Text("Helpful Resources") }
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", if (breakpoint <= Breakpoint.MD) "minmax(0, 1fr)" else "repeat(2, minmax(0, 1fr))")
                    property("gap", if (breakpoint <= Breakpoint.MD) "14px" else "24px")
                    property("align-items", "stretch")
                }
            }) {
                chatbotResources.forEach { resource ->
                    ChatbotResourceCard(resource, breakpoint, palette)
                }
            }
            P(attrs = {
                style {
                    margin(if (breakpoint <= Breakpoint.MD) 20.px else 28.px, 0.px, 0.px)
                    fontFamily(Constants.FONT_FAMILY)
                    fontSize(16.px)
                    lineHeight("1.6")
                    textAlign("center")
                }
            }) {
                Text("Compare chatbot development with our other AI solutions: ")
                DesktopNavigationAnchor("/solutions", Modifier.styleModifier {
                    color(palette.primary.rgb)
                    fontWeight(700)
                    textDecoration("underline")
                    property("text-underline-offset", "3px")
                }) {
                    Text("explore AI solutions for your business")
                }
                Text(".")
            }
        }
    }
}

@Composable
private fun ChatbotResourceCard(resource: ChatbotResource, breakpoint: Breakpoint, palette: SitePalette) {
    val pageContext = rememberPageContext()
    var article by remember(resource.slug) { mutableStateOf<SanityBlogPost?>(null) }
    var isHovered by remember { mutableStateOf(false) }

    DisposableEffect(resource.slug) {
        var active = true
        fetchPublishedPosts()
            .then { result ->
                if (active) article = result.find { it.slug == resource.slug }
                Unit
            }
            .catch { null }
        onDispose { active = false }
    }

    val coverUrl = article?.coverImageUrl.orEmpty()
    var coverFailed by remember(coverUrl) { mutableStateOf(false) }
    val title = article?.title?.takeIf(String::isNotBlank) ?: resource.title
    val alt = article?.coverImageAlt.orEmpty()

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            property("flex-direction", "column")
            property("min-width", "0")
            height(100.percent)
            borderRadius(8.px)
            backgroundColor(palette.softwareSectionBackground.rgb)
            property("border", "1px solid rgba(37, 77, 121, 0.14)")
            property("box-shadow", if (isHovered) "0 10px 26px rgba(37, 77, 121, 0.12)" else "0 4px 16px rgba(37, 77, 121, 0.06)")
            property("transform", if (isHovered) "translateY(-3px)" else "translateY(0)")
            property("transition", "transform 220ms ease, box-shadow 220ms ease")
            property("overflow", "hidden")
        }
        onMouseEnter { isHovered = true; prefetchPublishedPost(resource.slug) }
        onMouseLeave { isHovered = false }
    }) {
        Div(attrs = {
            style {
                width(100.percent)
                property("aspect-ratio", "16 / 9")
                property("position", "relative")
                property("overflow", "hidden")
                property("background-color", "rgba(37, 77, 121, 0.08)")
            }
        }) {
            if (coverUrl.isNotBlank() && !coverFailed) {
                Img(src = coverUrl, attrs = Modifier.imageLoading(coverUrl, lazy = true,
                    sizes = "(max-width: 767px) 90vw, 30vw").toAttrs {
                    attr("alt", alt)
                    attr("loading", "lazy")
                    ref { element ->
                        val listener = EventListener { coverFailed = true }
                        element.addEventListener("error", listener)
                        onDispose { element.removeEventListener("error", listener) }
                    }
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                })
            }
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                property("flex-direction", "column")
                property("align-items", "flex-start")
                property("flex", "1")
                property("padding", if (breakpoint <= Breakpoint.MD) "16px" else "20px")
            }
        }) {
            val articlePath = "/blogs/${resource.slug}"
            A(
                href = articlePath,
                attrs = Modifier.styleModifier {
                    color(palette.primary.rgb)
                    fontFamily(Constants.ROBOTO_FONT)
                    fontSize(if (breakpoint <= Breakpoint.MD) 18.px else 20.px)
                    fontWeight(700)
                    lineHeight("1.4")
                    textDecoration("underline")
                    property("text-underline-offset", "3px")
                }.onDesktopNavigationClick { pageContext.router.navigateTo(articlePath) }.toAttrs()
            ) { Text(title) }
            P(attrs = {
                style {
                    margin(8.px, 0.px, 16.px)
                    fontFamily(Constants.FONT_FAMILY)
                    fontSize(15.px)
                    lineHeight("1.6")
                }
            }) { Text(resource.description) }
        }
    }
}

@Composable
private fun SectionHeading(title: String, breakpoint: Breakpoint, headingId: String? = null) {
    H2(attrs = {
        headingId?.let { attr("id", it) }
        style {
            margin(0.px, 0.px, if (breakpoint <= Breakpoint.MD) 24.px else 32.px)
            fontFamily(Constants.ROBOTO_FONT)
            fontSize(if (breakpoint <= Breakpoint.MD) 25.px else 35.px)
            fontWeight(700)
            lineHeight("1.25")
            textAlign("center")
        }
    }) { Text(title) }
}

@Composable
fun AssistantFaq(breakpoint: Breakpoint, palette: SitePalette) {
    var openFaqIndex by remember { mutableStateOf<Int?>(null) }
    val faqTransition = if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        "none"
    } else {
        "grid-template-rows 250ms ease"
    }
    Section(attrs = {
        id("chatbot-faq")
        attr("aria-labelledby", "chatbot-faq-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(if (breakpoint <= Breakpoint.MD) 36.px else 60.px, if (breakpoint <= Breakpoint.MD) 20.px else 40.px)
            backgroundColor(palette.bodyBackgroundSecondary.rgb)
            color(palette.commonSecondaryButtonColor.rgb)
        }
    }) {
        Div(attrs = {
            style { property("max-width", "900px"); property("margin", "0 auto") }
        }) {
            H2(attrs = {
                attr("id", "chatbot-faq-heading")
                style {
                    margin(0.px, 0.px, if (breakpoint <= Breakpoint.MD) 24.px else 32.px)
                    fontFamily(Constants.ROBOTO_FONT)
                    fontSize(if (breakpoint <= Breakpoint.MD) 25.px else 35.px)
                    fontWeight(700)
                    lineHeight("1.25")
                    textAlign("center")
                }
            }) { Text("Frequently Asked Questions") }
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "minmax(0, 1fr)")
                    property("gap", "12px")
                }
            }) {
                assistantFaqs.forEachIndexed { index, faq ->
                    val questionId = "chatbot-faq-question-$index"
                    val answerId = "chatbot-faq-answer-$index"
                    val isOpen = openFaqIndex == index
                    Div(attrs = {
                        attr("role", "group")
                        attr("aria-labelledby", questionId)
                        style {
                            property("box-sizing", "border-box")
                            width(100.percent)
                            padding(if (breakpoint <= Breakpoint.MD) 14.px else 18.px)
                            borderRadius(8.px)
                            backgroundColor(palette.softwareSectionBackground.rgb)
                            property("border", "1px solid rgba(37, 77, 121, 0.14)")
                        }
                    }) {
                        H3(attrs = {
                            style {
                                margin(0.px)
                                fontFamily(Constants.ROBOTO_FONT)
                                fontSize(if (breakpoint <= Breakpoint.MD) 18.px else 20.px)
                                fontWeight(700)
                                lineHeight("1.4")
                            }
                        }) {
                            Button(attrs = {
                                attr("id", questionId)
                                attr("type", "button")
                                attr("aria-expanded", isOpen.toString())
                                attr("aria-controls", answerId)
                                onClick { openFaqIndex = if (isOpen) null else index }
                                style {
                                    display(DisplayStyle.Flex)
                                    property("align-items", "center")
                                    property("justify-content", "space-between")
                                    property("gap", "16px")
                                    width(100.percent)
                                    padding(0.px)
                                    border(0.px)
                                    backgroundColor(Color.transparent)
                                    color(palette.commonSecondaryButtonColor.rgb)
                                    fontFamily(Constants.ROBOTO_FONT)
                                    fontSize(if (breakpoint <= Breakpoint.MD) 18.px else 20.px)
                                    fontWeight(700)
                                    lineHeight("1.4")
                                    textAlign("left")
                                    property("cursor", "pointer")
                                }
                            }) {
                                Span { Text(faq.heading) }
                                Span(attrs = {
                                    attr("aria-hidden", "true")
                                    style {
                                        display(DisplayStyle.Flex)
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("flex", "0 0 24px")
                                        width(24.px)
                                        height(24.px)
                                        color(palette.primary.rgb)
                                        property("font-size", "18px")
                                    }
                                }) {
                                    if (isOpen) FaMinus() else FaPlus()
                                }
                            }
                        }
                        Div(attrs = {
                            id(answerId)
                            attr("class", "chatbot-faq-panel")
                            attr("role", "region")
                            attr("aria-labelledby", questionId)
                            attr("aria-hidden", (!isOpen).toString())
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-rows", if (isOpen) "1fr" else "0fr")
                                property("transition", faqTransition)
                                property("overflow", "hidden")
                            }
                        }) {
                            Div(attrs = {
                                style {
                                    property("min-height", "0")
                                    property("overflow", "hidden")
                                }
                            }) {
                                P(attrs = {
                                    style {
                                        margin(0.px)
                                        property("padding-top", "12px")
                                        fontFamily(Constants.FONT_FAMILY)
                                        fontSize(16.px)
                                        lineHeight("1.7")
                                    }
                                }) { Text(faq.body) }
                            }
                        }
                    }
                }
            }
        }
    }
}

